import { useState, useMemo } from 'react';
import {
  Container,
  Box,
  Heading,
  Text,
  Button,
  Spinner,
  useColorModeValue,
  VStack,
} from '@chakra-ui/react';
import { useHistory, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Icon } from '@chakra-ui/react';
import { useAuth } from '../../../contexts/AuthContext';
import { useHistoricalRocks } from './useHistoricalRocks';
import { useRocks } from '../Rocks/useRocks';
import { RockCardDetailed } from '../Rocks/components/RockCardDetailed';

/**
 * Historical Rock Detail Page
 * Displays detailed view of a single historical rock (achieved or dropped)
 * Reuses RockCardDetailed component in read-only mode
 */
export function HistoricalRockDetailPage() {
  const history = useHistory();
  const { rockId } = useParams<{ rockId: string }>();
  const { currentUser } = useAuth();
  const {
    rocks,
    users,
    loading: historicalLoading,
  } = useHistoricalRocks(currentUser?.uid);
  
  const {
    loadMilestonesForRock,
    loadActionItemsForRock,
    loadIssuesForRock,
    subscribeToIssues,
    loadPreviousNotes,
    rockAiScores,
  } = useRocks(currentUser?.uid);

  const [isExpanded, setIsExpanded] = useState(true);
  const refreshTrigger = 0;
  const refreshMilestonesTrigger = 0;
  const refreshActionItemsTrigger = 0;

  // Find the specific rock by ID
  const rock = useMemo(() => {
    if (!rockId) return null;
    return rocks.find((r) => r.id === rockId) || null;
  }, [rocks, rockId]);

  const headingColor = useColorModeValue('gray.900', 'white');
  const subheadingColor = useColorModeValue('gray.600', 'gray.400');
  const emptyBg = useColorModeValue('white', 'gray.700');
  const emptyBorder = useColorModeValue('gray.200', 'gray.600');
  const emptyText = useColorModeValue('gray.500', 'gray.400');

  // Get AI score for this rock
  const aiScore = rock ? (rockAiScores[rock.id] ?? null) : null;

  // Loading state
  if (historicalLoading) {
    return (
      <Container maxW="container.xl" py={8}>
        <Box textAlign="center" py={12}>
          <Spinner size="xl" />
          <Text color={subheadingColor} mt={4}>
            Loading historical rock...
          </Text>
        </Box>
      </Container>
    );
  }

  // Rock not found
  if (!rock) {
    return (
      <Container maxW="container.xl" py={8}>
        <Box mb={6}>
          <Button
            leftIcon={<Icon as={ArrowLeft} w={4} h={4} />}
            variant="ghost"
            onClick={() => history.push('/eos/historical-rocks')}
            mb={4}
          >
            Back to Historical Rocks
          </Button>
        </Box>
        <Box
          textAlign="center"
          py={12}
          bg={emptyBg}
          borderRadius="lg"
          border="1px"
          borderColor={emptyBorder}
        >
          <Heading size="md" color={headingColor} mb={2}>
            Rock Not Found
          </Heading>
          <Text color={emptyText}>
            The historical rock you're looking for doesn't exist or has been removed.
          </Text>
        </Box>
      </Container>
    );
  }

  // No-op handlers for read-only mode
  const handleEdit = () => {
    // Historical rocks are read-only
  };

  const handleDelete = () => {
    // Historical rocks are read-only
  };

  const handleAchieve = () => {
    // Historical rocks are read-only
  };

  const handleDrop = () => {
    // Historical rocks are read-only
  };

  const handleEvaluate = () => {
    // Historical rocks are read-only - AI evaluation not available for historical rocks
  };

  const handleStatusChange = () => {
    // Historical rocks are read-only
  };

  const handleIssueClick = () => {
    // Historical rocks are read-only - issues can be viewed but not modified
  };

  const handleResolveIssue = async () => {
    // Historical rocks are read-only - issues cannot be resolved
  };

  return (
    <Container maxW="container.xl" py={8}>
      {/* Back Button */}
      <Box mb={6}>
        <Button
          leftIcon={<Icon as={ArrowLeft} w={4} h={4} />}
          variant="ghost"
          onClick={() => history.push('/eos/historical-rocks')}
        >
          Back to Historical Rocks
        </Button>
      </Box>

      {/* Page Header */}
      <Box mb={6}>
        <Heading size="lg" color={headingColor} mb={2}>
          Historical Rock Details
        </Heading>
        <Text color={subheadingColor}>
          {rock.isAchieved ? 'Achieved Rock' : 'Dropped Rock'} - View only
        </Text>
      </Box>

      {/* Rock Card Detailed - Read Only */}
      <VStack spacing={4} align="stretch">
        <RockCardDetailed
          rock={rock}
          users={users}
          isExpanded={isExpanded}
          onToggleExpand={() => setIsExpanded(!isExpanded)}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onAchieve={handleAchieve}
          onDrop={handleDrop}
          onEvaluate={handleEvaluate}
          onStatusChange={handleStatusChange}
          onIssueClick={handleIssueClick}
          onResolveIssue={handleResolveIssue}
          refreshTrigger={refreshTrigger}
          refreshMilestonesTrigger={refreshMilestonesTrigger}
          refreshActionItemsTrigger={refreshActionItemsTrigger}
          aiScore={aiScore}
          loadMilestonesForRock={loadMilestonesForRock}
          loadActionItemsForRock={loadActionItemsForRock}
          loadIssuesForRock={loadIssuesForRock}
          subscribeToIssues={subscribeToIssues}
          saveRockUpdate={undefined}
          loadPreviousNotes={loadPreviousNotes}
          currentUserId={currentUser?.uid}
          // Don't pass add/toggle handlers for read-only mode
          addMilestone={undefined}
          toggleMilestone={undefined}
          addActionItem={undefined}
          toggleActionItem={undefined}
          isReadOnly={true}
        />
      </VStack>
    </Container>
  );
}

